import { useVisualizerStore } from '@/stores/useVisualizerStore';
import { isEmbedded } from '@/utils/config';
import type { AppError } from '@/types';

const IS_EMBED = isEmbedded();

const ERROR_CONTENT: Record<AppError['category'], { title: string; message: string; cta: string }> =
  {
    NO_GARAGE_FLOOR_DETECTED: {
      title: "We couldn't find a garage floor",
      message:
        'Try taking a clearer photo with the garage floor centered and well-lit. Make sure the full surface is visible.',
      cta: 'Try Another Photo',
    },
    MODEL_LOAD_FAILED: {
      title: 'GarageVision engine failed to load',
      message:
        'This may be due to a slow connection or insufficient device storage. Please check your connection and try again.',
      cta: 'Retry',
    },
    INFERENCE_TIMEOUT: {
      title: 'Analysis timed out',
      message:
        'Your device may be processing slowly. Try using a smaller photo or a faster connection.',
      cta: 'Retry',
    },
    BROWSER_UNSUPPORTED: {
      title: 'Browser not supported',
      message:
        'Please use Chrome, Safari, Firefox, or Edge (last 2 versions). Your current browser lacks required features.',
      cta: 'Learn More',
    },
    MEMORY_EXHAUSTED: {
      title: 'Not enough memory',
      message:
        'Your device is low on memory. Close other apps or tabs and try again with a smaller photo.',
      cta: 'Try Again',
    },
    INVALID_IMAGE: {
      title: 'Invalid image',
      message: 'Please upload a valid JPG, PNG, or HEIC image that is at least 640×480 pixels.',
      cta: 'Upload Another',
    },
    RENDER_FAILED: {
      title: 'Preview failed to render',
      message: 'There was a problem creating your preview. Please try again.',
      cta: 'Retry',
    },
    RATE_LIMITED: {
      title: 'Too many requests',
      message: "You've hit the rate limit. Please wait a moment and try again.",
      cta: 'Try Again',
    },
    UNKNOWN: {
      title: 'Something went wrong',
      message: 'An unexpected error occurred. Please try again or contact support.',
      cta: 'Try Again',
    },
  };

export function ErrorScreen() {
  const error = useVisualizerStore((s) => s.error);
  const reset = useVisualizerStore((s) => s.reset);

  const content = error ? ERROR_CONTENT[error.category] : ERROR_CONTENT.UNKNOWN;

  const handleAction = () => {
    if (error?.recoverable && error.retryAction) {
      error.retryAction();
    } else {
      reset();
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center px-4 bg-gradient-radial-blue ${IS_EMBED ? 'min-h-full py-12' : 'h-full'}`}>
      <div className="w-full max-w-sm text-center glass-strong rounded-3xl p-8 shadow-3d-card animate-slide-up">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 ring-4 ring-red-50/80">
          <svg
            className="h-8 w-8 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
        </div>

        <h2 className="mb-2 text-xl font-bold text-slate-900">{content.title}</h2>
        <p className="mb-6 text-slate-500 text-sm leading-relaxed">{content.message}</p>

        <div className="flex flex-col gap-3">
          <button
            onClick={handleAction}
            className="rounded-xl bg-bss-blue px-6 py-3 font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-bss-blue-dark hover:-translate-y-0.5 hover:shadow-blue-600/35 active:translate-y-0"
          >
            {content.cta}
          </button>
          <button
            onClick={reset}
            className="rounded-xl px-6 py-3 font-medium text-slate-500 transition-all hover:bg-slate-100 hover:text-slate-700"
          >
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}
